// The ground map's pipeline: a directed graph from sensors, through processing blocks, to guns.
// Pure logic: no DOM, no canvas.
//
// A sensor detection becomes a Report. The report flows along every wire out of its sensor; each
// block adds latency and may drop, transform or route it. A report that reaches a gun becomes an
// order the gun may fire on.

import type { EnemyKind } from "../../shared/ground";

export type SensorKind = "acoustic" | "camera" | "drone";
export type BlockKind = "fuse" | "gate" | "predict" | "classRouter" | "sectorRouter";
export type GunKind = "mortar" | "howitzer" | "attackDrone";

export interface BlockDef {
  label: string;
  tag: string;
  description: string;
  latencyMs: number;
  /** Reports per second the block can process; more than that and reports queue. */
  capacity: number;
  outputs: string[];
}

export const FUSE_WINDOW_S = 1.0;
export const FUSE_INTERVAL_S = 0.8;
/** A Fuse block follows at most this many targets at once. */
export const FUSE_MAX_TRACKS = 6;
/** A Fuse track with no new report for this long is freed. */
export const FUSE_TRACK_TIMEOUT_S = 1.5;
/** A report that would wait longer than this in a block's queue is dropped as overloaded. */
export const MAX_QUEUE_WAIT_S = 0.5;
/**
 * A block that goes red trips into a cooldown for this long, dropping everything sent to it.
 * Red means more arrivals in the last second than its capacity, or a Fuse with every track slot in use.
 */
export const COOLDOWN_S = 3;

/**
 * Precision gain when `sensors` different sensors report the same target into one Fuse block:
 * the first overlap gives 10%, and each further sensor adds half the previous step
 * (10%, 15%, 17.5%, 18.75%, …), approaching 20%.
 */
export function overlapGain(sensors: number): number {
  if (sensors < 2) return 0;
  return 0.2 * (1 - 0.5 ** (sensors - 1));
}
export const GATE_MAX_ERROR = 15;
/** Sector Router splits north/south at this y. */
export const SECTOR_SPLIT_Y = 270;

export const BLOCKS: Record<BlockKind, BlockDef> = {
  fuse: {
    label: "Fuse",
    tag: "join",
    description:
      "Merges reports of the same target from every sensor wired in. Keeps the most precise position and any known class or velocity, and passes each target at most every 0.8 s. Overlapping sensors sharpen the fix: 2 sensors give 10% more precision than the best of them, 3 give 15%, 4 give 17.5%, up to 20%. Follows at most 6 targets at once.",
    latencyMs: 60,
    capacity: 20,
    outputs: ["out"],
  },
  gate: {
    label: "Precision Gate",
    tag: "filter",
    description: `Drops reports less precise than ±${GATE_MAX_ERROR} m, so guns don't waste shells on vague acoustic fixes.`,
    latencyMs: 30,
    capacity: 40,
    outputs: ["out"],
  },
  predict: {
    label: "Lead Predictor",
    tag: "transform",
    description:
      "Lets guns lead a moving target. Needs a velocity, which acoustic sensors can't measure. Allows for latency before it, not after it.",
    latencyMs: 80,
    capacity: 12,
    outputs: ["out"],
  },
  classRouter: {
    label: "Class Router",
    tag: "router",
    description:
      "Armour goes out 'heavy'; infantry, vehicles and unclassified targets go out 'light'. Mortars barely scratch tanks.",
    latencyMs: 40,
    capacity: 30,
    outputs: ["light", "heavy"],
  },
  sectorRouter: {
    label: "Sector Router",
    tag: "router",
    description: "Splits targets by which half of the map they're in, so each mortar only hears about its own road.",
    latencyMs: 20,
    capacity: 40,
    outputs: ["north", "south"],
  },
};

export const BLOCK_ORDER: BlockKind[] = ["fuse", "gate", "predict", "classRouter", "sectorRouter"];

export type GraphNode =
  | { id: string; type: "sensor"; sensor: SensorKind; label: string; x: number; y: number }
  | { id: string; type: "block"; block: BlockKind; x: number; y: number }
  | { id: string; type: "gun"; gun: GunKind; label: string; x: number; y: number };

export interface Wire {
  id: string;
  from: string;
  port: number;
  to: string;
}

export interface Graph {
  nodes: GraphNode[];
  wires: Wire[];
}

export function outputsOf(node: GraphNode): string[] {
  if (node.type === "sensor") return ["out"];
  if (node.type === "gun") return [];
  return BLOCKS[node.block].outputs;
}

export function hasInput(node: GraphNode): boolean {
  return node.type !== "sensor";
}

/** Why a wire from (from, port) to `to` is not allowed, or null when it is. */
export function wireProblem(g: Graph, from: string, port: number, to: string): string | null {
  const a = g.nodes.find((n) => n.id === from);
  const b = g.nodes.find((n) => n.id === to);
  if (!a || !b) return "missing node";
  if (a.id === b.id) return "a node can't feed itself";
  if (port < 0 || port >= outputsOf(a).length) return "no such output";
  if (!hasInput(b)) return "sensors have no input";
  if (g.wires.some((w) => w.from === from && w.port === port && w.to === to)) return "already wired";
  if (reaches(g, to, from)) return "that would make a loop";
  return null;
}

function reaches(g: Graph, start: string, target: string): boolean {
  const seen = new Set<string>();
  const stack = [start];
  while (stack.length) {
    const id = stack.pop()!;
    if (id === target) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const w of g.wires) if (w.from === id) stack.push(w.to);
  }
  return false;
}

export interface Report {
  trackId: number;
  /** Graph node id of the sensor that produced the original fix. */
  sensorId: string;
  /** Reported position (true position plus sensor error) at `seenAt`. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  hasVelocity: boolean;
  cls: EnemyKind | null;
  /** Position error radius of this fix. */
  error: number;
  /**
   * The target's true position at `seenAt`. Only the simulation uses it, to apply a precision gain
   * to the real position error; no block makes decisions from it.
   */
  truthX: number;
  truthY: number;
  seenAt: number;
  /** Set by the Lead Predictor: the sim time it predicted at. */
  predictedAt: number | null;
}

export interface Delivery {
  /** Graph node id of the gun that receives the report. */
  gun: string;
  report: Report;
  at: number;
}

export interface FlowEvent {
  at: number;
  nodeId?: string;
  wireId?: string;
  passed: boolean;
  reason?: string;
}

interface FuseTrack {
  reports: { report: Report; at: number }[];
  lastEmit: number;
  lastError: number;
  lastSeen: number;
}

export interface NodeLoad {
  /** Arrivals over the last second divided by capacity; above 1 the block is overloaded. */
  load: number;
  /** Targets a Fuse block is following, or null for other blocks. */
  tracks: number | null;
  /** Seconds of cooldown left; 0 when the block is working. */
  cooldown: number;
}

/** Runs reports through the graph. Holds each block's queue and the Fuse blocks' memory between reports. */
export class FlowEngine {
  private fuse = new Map<string, Map<number, FuseTrack>>();
  private busyUntil = new Map<string, number>();
  private arrivals = new Map<string, number[]>();
  private cooldownUntil = new Map<string, number>();

  constructor(private graph: () => Graph) {}

  reset(): void {
    this.fuse.clear();
    this.busyUntil.clear();
    this.arrivals.clear();
    this.cooldownUntil.clear();
  }

  /** Current load of a block at sim time `now`. */
  load(nodeId: string, now: number): NodeLoad {
    const node = this.graph().nodes.find((n) => n.id === nodeId);
    if (!node || node.type !== "block") return { load: 0, tracks: null, cooldown: 0 };
    const cooldown = Math.max(0, (this.cooldownUntil.get(nodeId) ?? 0) - now);
    const list = (this.arrivals.get(nodeId) ?? []).filter((t) => t > now - 1);
    this.arrivals.set(nodeId, list);
    const recent = list.filter((t) => t <= now).length;
    const tracks =
      node.block === "fuse"
        ? [...(this.fuse.get(nodeId)?.values() ?? [])].filter((tr) => now - tr.lastSeen <= FUSE_TRACK_TIMEOUT_S).length
        : null;
    return { load: recent / BLOCKS[node.block].capacity, tracks, cooldown };
  }

  inject(sensorNodeId: string, report: Report): { deliveries: Delivery[]; events: FlowEvent[] } {
    const g = this.graph();
    const nodes = new Map(g.nodes.map((n) => [n.id, n]));
    const deliveries: Delivery[] = [];
    const events: FlowEvent[] = [];

    const visit = (id: string, r: Report, t: number, depth: number) => {
      const node = nodes.get(id);
      if (!node || depth > 32) return;

      let out: { port: number; report: Report }[];
      let at = t;
      if (node.type === "sensor") {
        events.push({ at, nodeId: id, passed: true });
        out = [{ port: 0, report: r }];
      } else if (node.type === "gun") {
        events.push({ at, nodeId: id, passed: true });
        deliveries.push({ gun: node.id, report: r, at });
        return;
      } else {
        const def = BLOCKS[node.block];
        if (t < (this.cooldownUntil.get(id) ?? -Infinity)) {
          events.push({ at: t, nodeId: id, passed: false, reason: "cooling down" });
          return;
        }
        // Going red trips the block into cooldown.
        const list = (this.arrivals.get(id) ?? []).filter((x) => x > t - 1);
        list.push(t);
        this.arrivals.set(id, list);
        if (list.length > def.capacity) {
          this.trip(id, t);
          events.push({ at: t, nodeId: id, passed: false, reason: "overloaded: cooling down" });
          return;
        }
        // One report at a time: wait for the block to be free, or drop if the queue is too long.
        const start = Math.max(t, this.busyUntil.get(id) ?? -Infinity);
        if (start - t > MAX_QUEUE_WAIT_S + 1e-9) {
          events.push({ at: t, nodeId: id, passed: false, reason: "overloaded" });
          return;
        }
        this.busyUntil.set(id, start + 1 / def.capacity);
        at = start + def.latencyMs / 1000;
        const result = this.process(node.id, node.block, r, at);
        if (!result.ok && result.reason === "track table full") {
          this.trip(id, t);
          events.push({ at: t, nodeId: id, passed: false, reason: "track table full: cooling down" });
          return;
        }
        events.push({ at, nodeId: id, passed: result.ok, reason: result.ok ? undefined : result.reason });
        if (!result.ok) return;
        out = [result.out];
      }

      for (const { port, report } of out) {
        for (const w of g.wires) {
          if (w.from !== id || w.port !== port) continue;
          events.push({ at, wireId: w.id, passed: true });
          visit(w.to, report, at, depth + 1);
        }
      }
    };

    visit(sensorNodeId, report, report.seenAt, 0);
    return { deliveries, events };
  }

  private process(
    nodeId: string,
    kind: BlockKind,
    r: Report,
    at: number,
  ): { ok: true; out: { port: number; report: Report } } | { ok: false; reason: string } {
    switch (kind) {
      case "gate":
        return r.error > GATE_MAX_ERROR ? { ok: false, reason: "imprecise" } : { ok: true, out: { port: 0, report: r } };
      case "predict":
        return { ok: true, out: { port: 0, report: r.hasVelocity ? { ...r, predictedAt: at } : r } };
      case "classRouter":
        return { ok: true, out: { port: r.cls === "armor" ? 1 : 0, report: r } };
      case "sectorRouter":
        return { ok: true, out: { port: r.y < SECTOR_SPLIT_Y ? 0 : 1, report: r } };
      case "fuse":
        return this.fuseReport(nodeId, r, at);
    }
  }

  private fuseReport(
    nodeId: string,
    r: Report,
    at: number,
  ): { ok: true; out: { port: number; report: Report } } | { ok: false; reason: string } {
    let tracks = this.fuse.get(nodeId);
    if (!tracks) this.fuse.set(nodeId, (tracks = new Map()));
    let track = tracks.get(r.trackId);
    if (!track || at - track.lastSeen > FUSE_TRACK_TIMEOUT_S) {
      for (const [id, tr] of tracks) if (at - tr.lastSeen > FUSE_TRACK_TIMEOUT_S) tracks.delete(id);
      if (tracks.size >= FUSE_MAX_TRACKS) return { ok: false, reason: "track table full" };
      tracks.set(r.trackId, (track = { reports: [], lastEmit: -Infinity, lastError: Infinity, lastSeen: at }));
    }
    track.lastSeen = at;

    track.reports.push({ report: r, at });
    track.reports = track.reports.filter((x) => at - x.at <= FUSE_WINDOW_S);

    // Re-emit early only when the new fix is much better than the last one sent.
    const due = at - track.lastEmit >= FUSE_INTERVAL_S;
    const better = r.error < track.lastError / 2;
    if (!due && !better) return { ok: false, reason: "merged" };

    const window = track.reports.map((x) => x.report);
    const best = window.reduce((a, b) => (b.error < a.error ? b : a));
    const withVelocity = window.filter((x) => x.hasVelocity).sort((a, b) => a.error - b.error)[0];
    const cls = window.find((x) => x.cls !== null)?.cls ?? null;
    // Overlapping sensors: shrink the best fix's real error by the overlap gain.
    const keep = 1 - overlapGain(new Set(window.map((x) => x.sensorId)).size);
    const fused: Report = {
      ...best,
      x: best.truthX + (best.x - best.truthX) * keep,
      y: best.truthY + (best.y - best.truthY) * keep,
      error: best.error * keep,
      cls,
      vx: withVelocity?.vx ?? 0,
      vy: withVelocity?.vy ?? 0,
      hasVelocity: !!withVelocity,
      predictedAt: best.predictedAt,
    };
    track.lastEmit = at;
    track.lastError = fused.error;
    return { ok: true, out: { port: 0, report: fused } };
  }

  /** Puts a block into cooldown: its queue is flushed and a Fuse forgets its tracks. */
  private trip(id: string, t: number): void {
    this.cooldownUntil.set(id, t + COOLDOWN_S);
    this.arrivals.set(id, []);
    this.busyUntil.delete(id);
    this.fuse.delete(id);
  }

  forget(trackId: number): void {
    for (const tracks of this.fuse.values()) tracks.delete(trackId);
  }
}

/**
 * Where a gun should aim at fire time `t` with shell flight time `flight`.
 * A predicted report is led forward from its fix, but the predictor only knew about time up to
 * `predictedAt`; latency between the predictor and the gun (`receivedAt - predictedAt`) is not
 * allowed for. The gun itself does allow for how long it held the order. An unpredicted report is
 * fired at where it was seen.
 */
export function aimPoint(r: Report, receivedAt: number, t: number, flight: number): { x: number; y: number } {
  if (r.predictedAt === null || !r.hasVelocity) return { x: r.x, y: r.y };
  const lead = r.predictedAt - r.seenAt + (t - receivedAt) + flight;
  return { x: r.x + r.vx * lead, y: r.y + r.vy * lead };
}
